/**
 * <GiftAutocomplete> — debounced username picker for the Builder's
 * "🎁 Gift to" field.
 *
 * Hits `GET /api/pal/gift-suggestions?q=<>` (which unions web_users
 * and linked_players_multi server-side) and renders matches under the
 * input. Each suggestion shows source (web vs Discord-only) so the
 * sender knows whether the recipient has actually logged into the
 * site — handy diagnostic when a gift fails with "user not linked".
 *
 * Keyboard nav: ↑/↓ to highlight, Enter to pick, Escape to close.
 * Click-outside closes the dropdown.
 *
 * Used at /builder inside <BuilderSendSection>.
 */
import { useEffect, useRef, useState } from "react";
import { Globe, Hash } from "lucide-react";
import { api } from "@/lib/api";

const MIN_CHARS = 2;
const DEBOUNCE_MS = 180;

export function GiftAutocomplete({ value, onChange, placeholder, testIdPrefix = "builder-gift-to" }) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [highlighted, setHighlighted] = useState(-1);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  // Track which query the in-flight request is for so stale responses
  // (slow network) don't overwrite a fresher result.
  const lastQueryRef = useRef("");

  // Debounced fetch — fires DEBOUNCE_MS after the user stops typing.
  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < MIN_CHARS) {
      setSuggestions([]);
      return undefined;
    }
    lastQueryRef.current = q;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/pal/gift-suggestions?q=${encodeURIComponent(q)}`);
        // Drop the response if a newer query has fired in the meantime.
        if (lastQueryRef.current !== q) return;
        setSuggestions(data?.suggestions || []);
        setHighlighted(-1);
      } catch {
        if (lastQueryRef.current === q) setSuggestions([]);
      } finally {
        if (lastQueryRef.current === q) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [value]);

  // Click-outside → close dropdown
  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const choose = (s) => {
    onChange(s.username);
    setOpen(false);
    setHighlighted(-1);
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => (h + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => (h <= 0 ? suggestions.length - 1 : h - 1));
    } else if (e.key === "Enter" && highlighted >= 0) {
      e.preventDefault();
      choose(suggestions[highlighted]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showDropdown = open && (value || "").trim().length >= MIN_CHARS &&
    (loading || suggestions.length > 0);

  return (
    <div ref={wrapRef} className="relative">
      <input
        ref={inputRef}
        id="builder-gift-to-input"
        type="text"
        placeholder={placeholder || "Discord username (e.g. friend, no #tag)"}
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className="w-full bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-sm focus:border-[var(--teal-soft)] outline-none"
        data-testid={`${testIdPrefix}-input`}
        maxLength={64}
        autoComplete="off"
        spellCheck="false"
      />
      {showDropdown && (
        <ul
          className="absolute z-30 mt-1 left-0 right-0 max-h-56 overflow-y-auto rounded-md border border-white/10 bg-[#0f0a14]/95 backdrop-blur-md shadow-xl"
          role="listbox"
          data-testid={`${testIdPrefix}-suggestions`}
        >
          {loading && suggestions.length === 0 && (
            <li className="px-3 py-2 text-[11px] text-[var(--muted)] italic"
                data-testid={`${testIdPrefix}-loading`}>
              Searching…
            </li>
          )}
          {suggestions.map((s, i) => (
            <li
              key={`${s.source}:${s.username}`}
              role="option"
              aria-selected={i === highlighted}
              onMouseEnter={() => setHighlighted(i)}
              onMouseDown={(e) => { e.preventDefault(); choose(s); }}
              className={
                "flex items-center gap-2 px-2.5 py-1.5 cursor-pointer text-sm " +
                (i === highlighted ? "bg-white/10" : "hover:bg-white/5")
              }
              data-testid={`${testIdPrefix}-suggestion-${s.username}`}
            >
              {/* Initial-letter chip. Discord avatars stored as a hash
                  without the user-id can't form a CDN URL; rendering a
                  letter chip avoids broken-image flashes. */}
              <span className="w-5 h-5 rounded-full bg-white/10 grid place-items-center text-[10px] uppercase font-bold text-amber-200 flex-shrink-0">
                {s.username.charAt(0)}
              </span>
              <span className="flex-1 truncate font-mono text-[12px]">{s.username}</span>
              <span
                className="text-[9px] uppercase tracking-wider text-[var(--muted)] flex items-center gap-0.5"
                title={s.source === "web" ? "Has signed into the website" : "Linked via /link in Discord (no website account yet)"}
              >
                {s.source === "web" ? <Globe size={9} /> : <Hash size={9} />}
                {s.source === "web" ? "web" : "discord"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
